import _ from 'lodash'
import { LitElement, html, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import style from './style/zaptic-dynamic-conditions-style'
import type { SingleCondition } from './zaptic-dynamic-condition'

export enum Events {
    onUpdated = 'on-updated',
}

export interface CustomEventPayloads {
    [Events.onUpdated]: { value: TopLevelJsonLogic; isValid: boolean }
}

export type TopLevelJsonLogic = {
    or: AndLogic[]
}

type AndLogic = {
    and: SingleCondition[]
}

@customElement('zaptic-dynamic-conditions')
export class ZapticDynamicConditions extends LitElement {
    static styles = style

    @property({ type: Array })
    variableNames: string[]

    @property({ type: Object })
    value: TopLevelJsonLogic

    @property({ type: Boolean })
    readonly: boolean

    @property()
    variablePlaceholder: string

    @property()
    conditionPlaceholder: string

    @property()
    valuePlaceholder: string

    @state()
    _areConditionsValid = false

    @state()
    private _newOrCondition = false

    @state()
    private _hasInitialized = false

    @state()
    private _isOnlyCondition = false

    firstUpdated() {
        this._newOrCondition = !this.value?.or.length
        this._hasInitialized = true
    }

    willUpdate(changedProperties: PropertyValues<ZapticDynamicConditions>) {
        this.init()
        if (changedProperties.has('value')) {
            this._areConditionsValid = this.validateConditions(this.value)
            if (this._areConditionsValid && this._newOrCondition) {
                this._newOrCondition = false
            }
            this._isOnlyCondition = this.value?.or.length === 1 && this.value?.or[0].and.length === 1
        }
    }

    private validateConditions(conditions: TopLevelJsonLogic | null): boolean {
        return conditions === null || conditions.or.every(({ and }) => and.every(Boolean))
    }

    private init() {
        if (this._hasInitialized) return
        if (!this.value) {
            this.setValue([{ and: [null] }])
        }
    }

    private addOrCondition() {
        this.setValue(this.value.or.concat([{ and: [null] }]))
    }

    private addAndCondition(topLevelIndexToAdd: number) {
        const newValue = this.value.or.slice()
        newValue[topLevelIndexToAdd].and.push(null)
        this.setValue(newValue)
    }

    private setValue(conditions: AndLogic[]) {
        this._areConditionsValid = this.validateConditions({ or: conditions })
        this.dispatchEvent(
            new CustomEvent<CustomEventPayloads['on-updated']>(Events.onUpdated, {
                detail: { value: { or: conditions }, isValid: this._areConditionsValid },
            })
        )
    }

    private onConditionUpdated(topLevelIndex: number, andLevelIndex: number, jsonLogic: SingleCondition) {
        const payload = this.getTopLevelLogicForModifiedElement(topLevelIndex, andLevelIndex, jsonLogic)
        const hasPayloadNotChanged = payload === this.value.or
        if (hasPayloadNotChanged) {
            return
        }
        this.setValue(payload)
    }

    private getTopLevelLogicForModifiedElement(
        topLevelIndex: number,
        andLevelIndex: number,
        jsonLogic: SingleCondition
    ): AndLogic[] {
        const payload = this.value.or.slice()
        payload[topLevelIndex].and[andLevelIndex] = jsonLogic
        return payload
    }

    private deleteCondition(topLevelIndex: number, andLevelIndex: number) {
        const copyOfCurrentValue = JSON.parse(JSON.stringify(this.value.or))
        copyOfCurrentValue[topLevelIndex].and.splice(andLevelIndex, 1)
        const hasAndConditionBecomeEmpty = !copyOfCurrentValue[topLevelIndex].and.length
        if (hasAndConditionBecomeEmpty) {
            copyOfCurrentValue.splice(topLevelIndex, 1)
        }
        this.setValue(copyOfCurrentValue)
    }

    private AndConditions(topLevelIndex: number, { andLogic }: { andLogic?: AndLogic } = {}) {
        const isLastAndConditionValid = !!andLogic.and.slice(-1)[0]
        return html`<div class="and-condition">
            <div class="single-condition-group">
                ${andLogic.and.map(
                    (singleCondition, index) =>
                        html`${AndSeparatorText(index)}
                            <zaptic-dynamic-condition
                                variablePlaceholder=${this.variablePlaceholder}
                                conditionPlaceholder=${this.conditionPlaceholder}
                                valuePlaceholder=${this.valuePlaceholder}
                                ?deletable=${!this._isOnlyCondition}
                                ?readonly=${this.readonly}
                                .variableNames=${this.variableNames}
                                .value=${singleCondition}
                                @on-updated=${(e: CustomEvent<SingleCondition>) => {
                                    this.onConditionUpdated(topLevelIndex, index, e.detail)
                                }}
                                @on-deleted=${() => {
                                    this.deleteCondition(topLevelIndex, index)
                                }}
                            ></zaptic-dynamic-condition>`
                )}
            </div>
            ${!this.readonly
                ? html`<zaptic-button
                      disabled=${!isLastAndConditionValid}
                      type="tertiary"
                      size="small"
                      @click=${() => {
                          if (isLastAndConditionValid) this.addAndCondition(topLevelIndex)
                      }}
                  >
                      <span slot="icon"> + </span>
                      <span class="is-uppercase"> ${window.i18n('shared:common.and')} </span>
                  </zaptic-button>`
                : null}
        </div>`
    }

    render() {
        return html`
            ${this.value !== null
                ? html`<div class="container">
                      ${this.value.or.map(
                          (andLogic, index) =>
                              html`${OrSeparatorText(index)} ${this.AndConditions(index, { andLogic })} `
                      )}
                      ${!this.readonly
                          ? html` <zaptic-button
                                disabled=${!this._areConditionsValid}
                                type="tertiary"
                                size="small"
                                @click=${() => {
                                    if (this._areConditionsValid) this.addOrCondition()
                                }}
                            >
                                <span slot="icon"> + </span>
                                <span class="is-uppercase">${window.i18n('shared:common.or')}</span>
                            </zaptic-button>`
                          : null}
                  </div>`
                : null}
        `
    }
}

function AndSeparatorText(index: number) {
    return !!index ? html` <span class="and-separator is-uppercase"> ${window.i18n('shared:common.and')} </span>` : null
}

function OrSeparatorText(index: number) {
    return !!index ? html` <span class="or-separator is-uppercase"> ${window.i18n('shared:common.or')} </span>` : null
}
declare global {
    interface HTMLElementTagNameMap {
        'zaptic-dynamic-conditions': ZapticDynamicConditions
    }
}
